import React from 'react'
import { Box, Checkbox, TextField, Typography } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'
import type { ListStatusTone } from '@/components/common/status'

/**
 * ===== Token style phần mở rộng dòng (expand) — dùng chung mọi màn danh sách =====
 * Sửa tại đây là áp cho tất cả panel / field expand.
 */
/** Chiều cao 1 dòng label + value. */
const ROW_H = 28
const ROW_GAP_PX = 4
/** Ô ghi chú cao bằng 5 hàng cột 2 (5×ROW_H + 4×gap) để đáy đường kẻ thẳng hàng. */
const NOTE_H = ROW_H * 5 + ROW_GAP_PX * 4
/** Độ rộng cột label — một giá trị cho mọi panel để value thẳng hàng giữa các khối. */
const LABEL_W = '160px'
/** Giữ tên cũ cho các màn đang truyền `labelWidth={EXPAND_LABEL_W_LONG}`. */
export const EXPAND_LABEL_W_LONG = LABEL_W
/** Nền + viền khối (panel) expand. */
export const EXPAND_PANEL_BG = '#f3f4f6'
export const EXPAND_PANEL_BORDER = '#d8dde5'
/** Nền ô giá trị: sửa được → trắng, chỉ xem → xám. */
export const EXPAND_VALUE_BG_EDITABLE = '#ffffff'
export const EXPAND_VALUE_BG_READONLY = 'rgba(16, 24, 40, 0.05)'

const FONT_PX = banhangKiotLike.kiotFontSizePx
const VALUE_H = ROW_H - 2

const fieldRowSxFor = (labelWidth: string = LABEL_W) =>
  ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: `${labelWidth} minmax(0, 1fr)` },
    columnGap: 1.5,
    alignItems: 'center',
    minWidth: 0,
    minHeight: ROW_H,
    height: ROW_H,
    py: 0,
    width: '100%',
    boxSizing: 'border-box',
  }) as const

const fieldFont = {
  fontFamily: banhangKiotLike.kiotFontStack,
  fontSize: `${FONT_PX}px`,
  lineHeight: 1.35,
} as const

const labelCellSx = {
  ...fieldFont,
  color: banhangKiotLike.kiotTextMuted,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  height: ROW_H,
  display: 'flex',
  alignItems: 'center',
  lineHeight: `${VALUE_H}px`,
} as const

const valueFillSx = (editable: boolean) =>
  ({
    bgcolor: editable ? EXPAND_VALUE_BG_EDITABLE : EXPAND_VALUE_BG_READONLY,
    px: 0.75,
    borderRadius: '3px 3px 0 0',
  }) as const

const valueUnderlineSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
  height: ROW_H,
  minHeight: ROW_H,
  borderBottom: '1px solid',
  borderColor: '#d0d5dd',
  pb: 0,
  boxSizing: 'border-box',
} as const

/** Ép mọi control (text / select / autocomplete / datetime) cùng cỡ chữ + cùng baseline. */
export const expandFlatEditorSx = {
  width: '100%',
  minWidth: 0,
  flex: '1 1 auto',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  '& .kiot-form-field': {
    width: '100% !important',
    minWidth: '0 !important',
    maxWidth: '100%',
    m: 0,
    p: 0,
    height: `${VALUE_H}px`,
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
  },
  '& .MuiFormControl-root': {
    width: '100% !important',
    minWidth: '0 !important',
    m: '0 !important',
  },
  '& .MuiFormHelperText-root': { display: 'none' },
  '& .MuiInputLabel-root, & .MuiFormLabel-root': { display: 'none' },
  '& .MuiInputBase-root:not(.MuiInputBase-multiline), & .MuiOutlinedInput-root:not(.MuiInputBase-multiline), & .MuiInputBase-root.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)': {
    ...fieldFont,
    fontWeight: 500,
    color: `${banhangKiotLike.kiotTextPrimary} !important`,
    bgcolor: 'transparent !important',
    boxShadow: 'none !important',
    borderRadius: '0 !important',
    minHeight: `${VALUE_H}px !important`,
    height: `${VALUE_H}px !important`,
    maxHeight: `${VALUE_H}px !important`,
    width: '100% !important',
    p: '0 !important',
    px: '0 !important',
    py: '0 !important',
    pl: '0 !important',
    pr: '0 !important',
    border: 'none !important',
    borderBottom: 'none !important',
    borderWidth: '0 !important',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot, & .MuiAutocomplete-inputRoot': {
    p: '0 !important',
    px: '0 !important',
    pl: '0 !important',
    pr: '22px !important',
    py: '0 !important',
  },
  '& .kiot-form-field .MuiSelect-select, & .MuiSelect-select, & .MuiSelect-select.MuiInputBase-input': {
    ...fieldFont,
    fontWeight: 500,
    fontSize: `${FONT_PX}px !important`,
    display: 'block !important',
    textAlign: 'left !important',
    justifyContent: 'unset !important',
    alignItems: 'unset !important',
    p: '0 !important',
    py: '0 !important',
    px: '0 !important',
    pl: '0 !important',
    pr: '22px !important',
    height: `${VALUE_H}px !important`,
    minHeight: `${VALUE_H}px !important`,
    maxHeight: `${VALUE_H}px !important`,
    lineHeight: `${VALUE_H}px !important`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  },
  '& .kiot-form-field .MuiSelect-select em, & .MuiSelect-select em': {
    fontStyle: 'normal',
    fontSize: `${FONT_PX}px !important`,
    display: 'block',
    textAlign: 'left',
    lineHeight: `${VALUE_H}px`,
  },
  // Chỉ ép chiều cao 1 dòng cho input thường — KHÔNG áp lên textarea (autosize).
  '& .MuiInputBase-input:not(.MuiInputBase-inputMultiline), & .MuiAutocomplete-input, & .MuiInputBase-inputSizeSmall:not(.MuiInputBase-inputMultiline), & input:not([type=checkbox]), & .kiot-form-field .MuiInputBase-input:not(.MuiInputBase-inputMultiline), & .kiot-form-field .MuiInputBase-inputSizeSmall:not(.MuiInputBase-inputMultiline)':
    {
      ...fieldFont,
      fontWeight: 500,
      fontSize: `${FONT_PX}px !important`,
      p: '0 !important',
      px: '0 !important',
      pl: '0 !important',
      pr: '0 !important',
      py: '0 !important',
      height: `${VALUE_H}px !important`,
      minHeight: `${VALUE_H}px !important`,
      maxHeight: `${VALUE_H}px !important`,
      lineHeight: `${VALUE_H}px !important`,
      textAlign: 'left !important',
      boxSizing: 'border-box',
    },
  '& .kiot-form-field .MuiSelect-select, & .kiot-form-field .MuiSelect-select.MuiInputBase-input': {
    fontSize: `${FONT_PX}px !important`,
    py: '0 !important',
    px: '0 !important',
    pl: '0 !important',
    pr: '22px !important',
    height: `${VALUE_H}px !important`,
    minHeight: `${VALUE_H}px !important`,
    lineHeight: `${VALUE_H}px !important`,
  },
  // Textarea: KHÔNG set height !important — để TextareaAutosize gán style.height.
  '& .MuiInputBase-inputMultiline, & textarea, & .kiot-form-field .MuiInputBase-input.MuiInputBase-inputMultiline':
    {
      ...fieldFont,
      fontWeight: 500,
      fontSize: `${FONT_PX}px !important`,
      minHeight: 'unset !important',
      maxHeight: 'none !important',
      lineHeight: '1.45 !important',
      p: '0 !important',
      px: '0 !important',
      py: '0 !important',
      textAlign: 'left !important',
      overflow: 'hidden !important',
      overflowY: 'hidden !important',
      resize: 'none',
      boxSizing: 'border-box',
    },
  '& .MuiOutlinedInput-root.MuiInputBase-multiline, & .MuiInputBase-root.MuiInputBase-multiline': {
    height: 'auto !important',
    minHeight: 'unset !important',
    maxHeight: 'none !important',
    alignItems: 'flex-start !important',
    py: '0 !important',
    px: '0 !important',
    bgcolor: 'transparent !important',
    boxShadow: 'none !important',
    borderRadius: '0 !important',
    border: 'none !important',
    width: '100% !important',
  },
  '& .kiot-form-field .MuiOutlinedInput-root fieldset, & .kiot-form-field .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
    {
      border: 'none !important',
      borderWidth: '0 !important',
      borderColor: 'transparent !important',
    },
  '& .kiot-form-field .MuiOutlinedInput-root:hover fieldset, & .kiot-form-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .kiot-form-field .MuiOutlinedInput-root.Mui-focused fieldset, & .kiot-form-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      border: 'none !important',
      borderWidth: '0 !important',
      borderColor: 'transparent !important',
    },
  '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before':
    {
      borderBottom: 'none !important',
      borderWidth: '0 !important',
    },
  // DateTime / Search / Select shell: bỏ gạch chân kép, ép cùng chiều cao hàng.
  '& .kiot-form-field > .MuiBox-root': {
    border: 'none !important',
    borderBottom: 'none !important',
    boxShadow: 'none !important',
    height: `${VALUE_H}px !important`,
    minHeight: `${VALUE_H}px !important`,
    maxHeight: `${VALUE_H}px !important`,
    justifyContent: 'flex-start !important',
    alignItems: 'center !important',
    p: '0 !important',
    px: '0 !important',
    pl: '0 !important',
    overflow: 'hidden',
  },
  '& .MuiAutocomplete-root': {
    width: '100%',
    minWidth: 0,
    height: `${VALUE_H}px`,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiSelect-icon': {
    right: 0,
    top: 'calc(50% - 0.5rem)',
    opacity: 0.55,
  },
  '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
    p: 0,
    width: 18,
    height: 18,
  },
  '& .MuiIconButton-root': {
    width: 18,
    height: 18,
  },
  '& .MuiInputBase-root.MuiInputBase-formControl': {
    gap: 0.5,
  },
} as const

const fieldRowSx = fieldRowSxFor(LABEL_W)

export type ExpandUnderlineFieldProps = {
  label: string
  value?: React.ReactNode
  emphasize?: boolean
  link?: boolean
  multiline?: boolean
  /**
   * Số dòng sàn khi multiline.
   * Không kèm `fillHeight` → tự tăng chiều cao theo nội dung (không scroll).
   */
  multilineRows?: number
  /** Chữ nghiêng, không đậm (vd. ghi chú giao hàng). */
  italic?: boolean
  placeholder?: string
  endAdornment?: 'edit' | 'search' | 'datetime' | 'info' | React.ReactNode
  /** Icon đầu ô (vd. edit trong textarea ghi chú). */
  startAdornment?: 'edit' | 'search' | 'datetime' | 'info' | React.ReactNode
  stacked?: boolean
  editor?: React.ReactNode
  /** Override độ rộng cột label (vd. EXPAND_LABEL_W_LONG). */
  labelWidth?: string
  /** Ô ghi chú: cao full cột để đường kẻ đáy dóng hàng cuối cột 2. */
  fillHeight?: boolean
  /** Tô nền ô giá trị (mặc định bật): có `editor` → trắng (sửa được), không → xám (chỉ xem). */
  filled?: boolean
}

const resolveAdornment = (
  adornment: ExpandUnderlineFieldProps['endAdornment'],
): React.ReactNode => {
  if (adornment === 'edit') {
    return (
      <EditOutlinedIcon sx={{ fontSize: 15, color: banhangKiotLike.kiotTextMuted, flexShrink: 0 }} />
    )
  }
  if (adornment === 'search') {
    return (
      <SearchOutlinedIcon sx={{ fontSize: 15, color: banhangKiotLike.kiotTextMuted, flexShrink: 0 }} />
    )
  }
  if (adornment === 'datetime') {
    return (
      <Box sx={{ display: 'flex', gap: 0.15, flexShrink: 0, color: banhangKiotLike.kiotTextMuted }}>
        <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />
        <AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />
      </Box>
    )
  }
  if (adornment === 'info') {
    return (
      <InfoOutlinedIcon sx={{ fontSize: 14, color: banhangKiotLike.kiotTextMuted, flexShrink: 0 }} />
    )
  }
  return adornment
}

/** Trạng thái dạng chữ màu theo tone (thay chip) — dùng làm `value` / `renderValue` trong expand. */
export const ExpandStatusText: React.FC<{ tone?: ListStatusTone; label: React.ReactNode }> = ({
  tone = 'default',
  label,
}) => (
  <Box
    component="span"
    sx={{
      fontWeight: 600,
      color: tone === 'default' ? 'text.secondary' : `${tone}.dark`,
      // Select posUnderline ép -webkit-text-fill-color đen → phải theo màu chữ.
      WebkitTextFillColor: 'currentColor',
    }}
  >
    {label}
  </Box>
)

/** Icon edit dùng làm startAdornment trong TextArea ghi chú expand. */
export const expandNoteEditIcon = (
  <EditOutlinedIcon sx={{ fontSize: 15, color: banhangKiotLike.kiotTextMuted }} />
)

/** Ô label + value gạch chân kiểu Kiot expand (read-only hoặc editor). */
export const ExpandUnderlineField: React.FC<ExpandUnderlineFieldProps> = ({
  label,
  value,
  emphasize,
  link,
  multiline,
  multilineRows,
  italic,
  placeholder,
  endAdornment,
  startAdornment,
  stacked,
  editor,
  labelWidth,
  fillHeight,
  filled = true,
}) => {
  const empty =
    value == null ||
    value === '' ||
    value === '—' ||
    (typeof value === 'string' && value.trim() === '')
  const display = empty ? (
    <Box
      component="span"
      sx={{
        color: banhangKiotLike.kiotTextMuted,
        fontWeight: 400,
        fontStyle: italic ? 'italic' : undefined,
      }}
    >
      {placeholder ?? ''}
    </Box>
  ) : (
    value
  )

  const endAdornmentNode = resolveAdornment(endAdornment)
  const startAdornmentNode = resolveAdornment(startAdornment)

  /** Sàn N dòng do TextAreaAutosize (minRows) — không ép minHeight wrapper theo ROW_H (dễ thành 3 dòng). */
  const isAutoGrow = Boolean(multiline && multilineRows && multilineRows > 0 && !fillHeight)
  const isFillNote = Boolean(multiline && fillHeight)
  const isTallNote = Boolean(isAutoGrow || isFillNote || (multiline && stacked))
  const fillMinH = NOTE_H
  const italicSx = italic
    ? { fontStyle: 'italic !important', fontWeight: '400 !important' }
    : null

  const startIconBox =
    startAdornmentNode != null && startAdornmentNode !== false ? (
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          pt: isTallNote ? '2px' : 0,
          pr: 0.75,
          alignSelf: isTallNote ? 'flex-start' : 'center',
        }}
      >
        {startAdornmentNode}
      </Box>
    ) : null

  const valueBox = (
    <Box
      className={isTallNote ? 'expand-field-value expand-field-value--tall' : 'expand-field-value'}
      sx={{
        ...valueUnderlineSx,
        alignItems: isTallNote ? 'flex-start' : 'center',
        height: isFillNote ? '100%' : isAutoGrow ? 'auto' : isTallNote ? fillMinH : ROW_H,
        minHeight: isFillNote ? fillMinH : isAutoGrow ? 0 : isTallNote ? fillMinH : ROW_H,
        flex: fillHeight ? 1 : undefined,
        alignSelf: fillHeight ? 'stretch' : undefined,
        pt: isTallNote ? '2px' : 0,
        pb: isTallNote ? '2px' : 0,
        overflow: isAutoGrow ? 'visible' : undefined,
        ...(filled ? valueFillSx(Boolean(editor)) : null),
      }}
    >
      {startIconBox}
      {editor ? (
        <Box
          sx={{
            ...expandFlatEditorSx,
            flex: 1,
            minWidth: 0,
            alignItems: isTallNote ? 'flex-start' : 'center',
            alignSelf: isTallNote ? 'stretch' : undefined,
            height: isFillNote ? '100%' : 'auto',
            overflow: isAutoGrow ? 'visible' : isFillNote ? 'auto' : 'hidden',
            ...(isFillNote
              ? {
                  '& .kiot-form-field': {
                    height: '100% !important',
                    minHeight: `${fillMinH - 8}px`,
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                    height: '100% !important',
                    minHeight: `${fillMinH - 8}px !important`,
                    maxHeight: 'none !important',
                    alignItems: 'flex-start !important',
                  },
                  '& .MuiInputBase-inputMultiline, & textarea': {
                    height: '100% !important',
                    minHeight: `${fillMinH - 16}px !important`,
                    overflowY: 'auto !important',
                    ...italicSx,
                  },
                  '& .MuiInputAdornment-root': {
                    height: 'auto',
                    maxHeight: 'none',
                    alignSelf: 'flex-start',
                    mt: '2px',
                    mr: 0.75,
                  },
                }
              : null),
            ...(isAutoGrow
              ? {
                  '& .kiot-form-field': {
                    height: 'auto !important',
                    minHeight: '0 !important',
                    maxHeight: 'none !important',
                    alignItems: 'flex-start',
                    overflow: 'visible',
                  },
                  '& .MuiFormControl-root': {
                    height: 'auto !important',
                    maxHeight: 'none !important',
                  },
                  '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiInputBase-multiline':
                    {
                      height: 'auto !important',
                      minHeight: '0 !important',
                      maxHeight: 'none !important',
                      alignItems: 'flex-start !important',
                      overflow: 'visible !important',
                      py: '0 !important',
                    },
                  // Không đụng height — TextareaAutosize set style.height theo minRows / nội dung.
                  '& .MuiInputBase-inputMultiline, & textarea, & .kiot-form-field .MuiInputBase-input.MuiInputBase-inputMultiline':
                    {
                      minHeight: '0 !important',
                      maxHeight: 'none !important',
                      overflow: 'hidden !important',
                      overflowY: 'hidden !important',
                      resize: 'none',
                      lineHeight: '1.45 !important',
                      ...italicSx,
                    },
                  '& .MuiInputAdornment-root': {
                    height: 'auto',
                    maxHeight: 'none',
                    alignSelf: 'flex-start',
                    mt: '1px',
                    mr: 0.75,
                  },
                }
              : null),
            ...(!isTallNote && italicSx
              ? {
                  '& .MuiInputBase-input, & textarea': italicSx,
                }
              : null),
          }}
        >
          {editor}
        </Box>
      ) : (
        <Typography
          component="div"
          sx={{
            ...fieldFont,
            flex: 1,
            minWidth: 0,
            fontWeight: italic ? 400 : emphasize ? 700 : 500,
            fontStyle: italic ? 'italic' : undefined,
            color: link ? banhangKiotLike.linkBlue : banhangKiotLike.kiotTextPrimary,
            whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
            overflow: isAutoGrow ? 'visible' : multiline ? 'auto' : 'hidden',
            textOverflow: multiline ? undefined : 'ellipsis',
            display: 'block',
            height: isAutoGrow ? 'auto' : '100%',
            lineHeight: isTallNote ? 1.45 : `${VALUE_H}px`,
            py: isTallNote ? '2px' : 0,
          }}
        >
          {display}
        </Typography>
      )}
      {endAdornmentNode ? (
        isTallNote ? (
          <Box sx={{ pt: '2px', flexShrink: 0, alignSelf: 'flex-start' }}>{endAdornmentNode}</Box>
        ) : (
          endAdornmentNode
        )
      ) : null}
    </Box>
  )

  if (stacked) {
    return (
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: label ? 0.25 : 0,
          height: fillHeight ? '100%' : undefined,
          flex: fillHeight ? 1 : undefined,
        }}
      >
        {label ? <Typography sx={labelCellSx}>{label}</Typography> : null}
        {valueBox}
      </Box>
    )
  }

  return (
    <Box
      className={isTallNote ? 'expand-field-row expand-field-row--tall' : 'expand-field-row'}
      sx={{
        ...(labelWidth ? fieldRowSxFor(labelWidth) : fieldRowSx),
        ...(isAutoGrow || (isTallNote && !fillHeight)
          ? { height: 'auto', minHeight: ROW_H, alignItems: 'flex-start' }
          : null),
      }}
    >
      <Typography
        className="expand-field-label"
        sx={{ ...labelCellSx, alignSelf: isTallNote ? 'flex-start' : 'center' }}
      >
        {label}
      </Typography>
      {valueBox}
    </Box>
  )
}

export type ExpandCheckboxFieldProps = {
  label: string
  checked: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
  labelWidth?: string
}

export const ExpandCheckboxField: React.FC<ExpandCheckboxFieldProps> = ({
  label,
  checked,
  disabled = true,
  onChange,
  labelWidth,
}) => (
  <Box sx={labelWidth ? fieldRowSxFor(labelWidth) : fieldRowSx}>
    <Typography sx={labelCellSx}>{label}</Typography>
    <Box sx={{ ...valueUnderlineSx, ...valueFillSx(!disabled) }}>
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange?.(next)}
        sx={{
          p: 0,
          m: 0,
          width: 20,
          height: 20,
          color: banhangKiotLike.posButtonGreen,
          '&.Mui-checked': { color: banhangKiotLike.posButtonGreen },
          '&.Mui-disabled': { color: banhangKiotLike.posButtonGreen, opacity: 0.9 },
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      />
    </Box>
  </Box>
)

export type ExpandDimFieldsProps = {
  label: string
  dai: React.ReactNode
  rong: React.ReactNode
  cao: React.ReactNode
  editable?: boolean
  onChange?: (next: { dai: string; rong: string; cao: string }) => void
  values?: { dai: string; rong: string; cao: string }
  disabled?: boolean
  labelWidth?: string
}

export const ExpandDimFields: React.FC<ExpandDimFieldsProps> = ({
  label,
  dai,
  rong,
  cao,
  editable = false,
  onChange,
  values,
  disabled = false,
  labelWidth,
}) => (
  <Box sx={labelWidth ? fieldRowSxFor(labelWidth) : fieldRowSx}>
    <Typography sx={labelCellSx}>{label}</Typography>
    <Box sx={{ display: 'flex', gap: 0.75, minWidth: 0, height: ROW_H, alignItems: 'stretch' }}>
      {(editable
        ? [
            { key: 'dai' as const, val: values?.dai ?? '' },
            { key: 'rong' as const, val: values?.rong ?? '' },
            { key: 'cao' as const, val: values?.cao ?? '' },
          ]
        : [
            { key: 'dai' as const, val: dai },
            { key: 'rong' as const, val: rong },
            { key: 'cao' as const, val: cao },
          ]
      ).map((item) => (
        <Box
          key={item.key}
          sx={{
            flex: 1,
            minWidth: 0,
            borderBottom: '1px solid',
            borderColor: '#d0d5dd',
            ...valueFillSx(editable && !disabled),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...fieldFont,
            fontWeight: 500,
            color: banhangKiotLike.kiotTextPrimary,
          }}
        >
          {editable ? (
            <TextField
              variant="standard"
              size="small"
              disabled={disabled}
              value={item.val}
              onChange={(e) => {
                const next = {
                  dai: values?.dai ?? '',
                  rong: values?.rong ?? '',
                  cao: values?.cao ?? '',
                  [item.key]: e.target.value,
                }
                onChange?.(next)
              }}
              InputProps={{ disableUnderline: true }}
              inputProps={{
                style: {
                  textAlign: 'center',
                  padding: 0,
                  height: VALUE_H,
                  fontSize: FONT_PX,
                  lineHeight: `${VALUE_H}px`,
                },
              }}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  ...fieldFont,
                  fontWeight: 500,
                  height: VALUE_H,
                  minHeight: VALUE_H,
                },
              }}
            />
          ) : item.val == null || item.val === '' || item.val === '—' ? (
            ''
          ) : (
            item.val
          )}
        </Box>
      ))}
    </Box>
  </Box>
)

/** Cột field expand — khoảng cách đều giữa các dòng. */
export const expandFieldColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${ROW_GAP_PX}px`,
  minWidth: 0,
  height: '100%',
  alignSelf: 'stretch',
} as const

/** Cột ghi chú — cao full panel để đáy đường kẻ dóng hàng cuối cột 2. */
export const expandNoteColumnSx = {
  ...expandFieldColumnSx,
  minHeight: NOTE_H,
} as const

/** Panel info / shipping expand — cột đủ rộng để select không cắt chữ. */
export type ExpandThuKhacFee = {
  id: string
  label: string
  value: string
}

/** Cột chi tiết thu khác — chỉ khoản có tiền, nhãn trái và số phải trong cột. */
export const ExpandThuKhacColumn: React.FC<{ title: string; fees: ExpandThuKhacFee[] }> = ({
  title,
  fees,
}) => {
  if (fees.length === 0) return null
  return (
    <Box sx={{ mt: 2, minWidth: 0, maxWidth: 360 }}>
      <Typography
        sx={{
          fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
          fontFamily: banhangKiotLike.kiotFontStack,
          fontWeight: 600,
          color: banhangKiotLike.kiotTextPrimary,
          lineHeight: 1.45,
          mb: 0.75,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {fees.map((fee) => (
          <Box
            key={fee.id}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 2,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
                fontFamily: banhangKiotLike.kiotFontStack,
                color: banhangKiotLike.kiotTextMuted,
                lineHeight: 1.45,
                minWidth: 0,
              }}
            >
              {fee.label}
            </Typography>
            <Typography
              sx={{
                fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
                fontFamily: banhangKiotLike.kiotFontStack,
                fontWeight: 600,
                color: banhangKiotLike.kiotTextPrimary,
                lineHeight: 1.45,
                whiteSpace: 'nowrap',
              }}
            >
              {fee.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

/** Lưới 3 cột (lg) / 2 cột (md) — chung cho khối thông tin và khối giao hàng để value thẳng hàng. */
const expandGridColumns = {
  xs: '1fr',
  md: 'repeat(2, minmax(0, 1fr))',
  lg: 'repeat(3, minmax(0, 1fr))',
} as const

/** Padding nội dung mỗi tab trong panel mở rộng dòng. */
export const expandTabPanelPadSx = {
  px: 0.75,
  py: 1,
  bgcolor: '#fff',
} as const

export const expandShippingPanelSx = {
  bgcolor: EXPAND_PANEL_BG,
  border: '1px solid',
  borderColor: EXPAND_PANEL_BORDER,
  borderRadius: '4px',
  px: 1.25,
  py: 1.25,
} as const

export const expandInfoPanelSx = {
  ...expandShippingPanelSx,
  display: 'grid',
  gridTemplateColumns: expandGridColumns,
  columnGap: { xs: 1.5, md: 2.5 },
  rowGap: `${ROW_GAP_PX}px`,
  alignItems: 'stretch',
  py: 0.75,
} as const

/** Lưới field giao hàng — 3 cột cùng tỷ lệ label để value thẳng hàng. */
export const expandShippingGridSx = {
  display: 'grid',
  gridTemplateColumns: expandGridColumns,
  columnGap: { xs: 1.5, md: 2.5 },
  rowGap: `${ROW_GAP_PX}px`,
} as const

export type ExpandShippingToggleHeaderProps = {
  label: string
  summary: string
  open: boolean
  onToggle: () => void
}

/** Header thu gọn/mở rộng khối giao hàng trong expand list. */
export const ExpandShippingToggleHeader: React.FC<ExpandShippingToggleHeaderProps> = ({
  label,
  summary,
  open,
  onToggle,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      width: '100%',
      border: 'none',
      bgcolor: 'transparent',
      cursor: 'pointer',
      p: 0,
      m: 0,
      textAlign: 'left',
      minHeight: 34,
      lineHeight: '34px',
      mb: open ? 1 : 0,
    }}
  >
    <Typography
      component="span"
      sx={{
        flex: '1 1 auto',
        minWidth: 0,
        fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
        fontFamily: banhangKiotLike.kiotFontStack,
        color: banhangKiotLike.kiotTextPrimary,
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}: {summary || '—'}
    </Typography>
    <ExpandMoreOutlinedIcon
      sx={{
        flex: '0 0 auto',
        fontSize: 20,
        color: banhangKiotLike.kiotTextMuted,
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.15s ease',
      }}
    />
  </Box>
)
